import { useEffect, useState } from "react";
import axios from "axios";
import { Home, FileText, Lightbulb, MessageSquare, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  ["properties", "dash.properties", Home],
  ["reports", "dash.reports", FileText],
  ["projects", "dash.projects", Lightbulb],
  ["questions", "dash.questions", MessageSquare],
];

export const Dashboard = ({ onOpenProperty }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("properties");
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/me/properties`).then((r) => setProperties(r.data || [])).catch(() => {});
    axios.get(`${API}/me/projects`).then((r) => setProjects(r.data || [])).catch(() => {});
    axios.get(`${API}/me/questions`).then((r) => setQuestions(r.data || [])).catch(() => {});
  }, []);

  const openProperty = async (folio) => {
    try {
      const { data } = await axios.get(`${API}/me/properties/${folio}`);
      onOpenProperty(data);
    } catch {}
  };

  const PropCard = (p) => (
    <div key={p.folio} data-testid="dash-property" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-2">
        <MapPin className="mt-0.5 h-4 w-4 text-primary" />
        <div>
          <p className="font-semibold text-foreground">{p.address || "—"}</p>
          <p className="font-mono text-xs text-muted-foreground">{p.folio} · {p.jurisdiction || "—"}{p.zoning ? ` · ${p.zoning}` : ""}</p>
        </div>
      </div>
      <Button variant="outline" size="sm" data-testid="dash-open-property" onClick={() => openProperty(p.folio)}>{t("dash.open")}</Button>
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="dashboard">
      <h1 className="mb-5 font-serif text-3xl font-semibold text-foreground">{t("dash.title")}</h1>
      <div className="mb-6 flex flex-wrap gap-1.5">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} data-testid={`dash-tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${tab === key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/50 text-foreground/70 hover:border-primary/40"}`}>
            <Icon className="h-3.5 w-3.5" />{t(label)}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {(tab === "properties" || tab === "reports") && (properties.length ? properties.map(PropCard) : <p className="text-sm text-muted-foreground">{t("dash.empty")}</p>)}
        {tab === "projects" && (projects.length ? projects.map((p, i) => (
          <div key={i} data-testid="dash-project" className="rounded-xl border border-border bg-card p-4">
            <p className="font-semibold capitalize text-foreground">{(p.event || "").replace("_", " ")}</p>
            <p className="font-mono text-xs text-muted-foreground">{p.meta?.project_type || p.meta?.folio || "—"} · {p.created_at?.slice(0, 10)}</p>
          </div>
        )) : <p className="text-sm text-muted-foreground">{t("dash.empty")}</p>)}
        {tab === "questions" && (questions.length ? questions.map((q, i) => (
          <div key={i} data-testid="dash-question" className="rounded-xl border border-border bg-card p-4">
            <p className="text-sm font-semibold text-foreground">{q.question}</p>
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{q.answer}</p>
            <p className="mt-1 font-mono text-[10px] text-muted-foreground">{q.address || "—"} · {q.created_at?.slice(0, 10)}</p>
          </div>
        )) : <p className="text-sm text-muted-foreground">{t("dash.empty")}</p>)}
      </div>
    </div>
  );
};
